import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import axios from 'axios';
import config from '../config.js';

import '../styles/global.css';
import '../styles/pagination.css';

import PageHeader from '../components/general/pageHeader.js';
import PageTable from '../components/general/pageTable.js';
import ParcelVisitorsCards from '../components/cards/parcelVisitorsCards';
import Loading from '../components/general/loading.js';
import useAuthenticate from '../components/authentication/authenticate.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const ParcelsVisitors = () => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [activeTab, setActiveTab] = useState('parcels');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 2;

    const { isLoading } = useAuthenticate();

    const { data: properties } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: units = [], isFetching: isUnitsLoading } = useQuery({
        queryKey: ['v2-units'],
        queryFn: fetchUnits,
    });

    const { data: visitors = [], isLoading: isVTableLoading } = useQuery({
        queryKey: ['v2-visitors', selectedUnit?._id],
        queryFn: () => fetchVisitorsByUnit(selectedUnit._id),
        enabled: !!selectedUnit,
    });

    const { data: parcels = { results: [] }, isLoading: isPTableLoading } = useQuery({
        queryKey: ['v2-parcels', selectedUnit?._id],
        queryFn: () => fetchParcelsByUnit(selectedUnit._id),
        enabled: !!selectedUnit,
    });

    const filteredUnits = selectedProperty
        ? units.filter(u => u.property?._id === selectedProperty?.value)
        : [];

    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
        setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    // Pagination logic
    const currentRows =
        activeTab === 'parcels'
        ? parcels.results?.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) || []
        : visitors.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) || [];

    const totalResults =
        activeTab === 'parcels'
        ? parcels.results?.length || 0
        : visitors.length || 0;

    const totalPages = Math.ceil(totalResults / rowsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, selectedUnit]);

    return (
        <div className="full-page w-full">
            <PageHeader pageName="Parcels and Visitors" />
            {isLoading && !selectedProperty ? (
                <Loading />
            ) : (
                <>
                    <div className="flex flex-col md:flex-row flex-end justify-between mt-[20px] pb-[15px] border-b-[1.5px] border-[#E8E8E8] gap-0">
                        <div className="account-dropdown md:mr-[15px]">
                            <div className="w-full md:w-[300px]">
                                <Autocomplete
                                    options={properties}
                                    value={selectedProperty}
                                    onChange={(e, newVal) => {
                                        if (newVal && newVal.value && newVal.label) {
                                            setSelectedProperty(newVal);
                                        } else {
                                            setSelectedProperty(null);
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                    getOptionLabel={(option) =>
                                        typeof option === 'string'
                                        ? option
                                        : option?.label || option?.name || ''
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                                    )}
                                    sx={{ marginBottom: '15px' }}
                                />
                            </div>
                            <div className="w-full mb-[15px] md:mb-0 md:w-[300px]">
                                <Autocomplete
                                    options={filteredUnits}
                                    value={selectedUnit}
                                    onChange={(e, val) => setSelectedUnit(val)}
                                    loading={isUnitsLoading}
                                    getOptionLabel={(option) => option.unitNumber || ''}
                                    isOptionEqualToValue={(option, value) => option.value === value._id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Unit"
                                            variant="outlined"
                                            fullWidth
                                            helperText={isUnitsLoading ? 'Loading units...' : ''}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex space-between w-full md:w-[240px] gap-3 mb-[15px] md:mb-0 mt-[20px]">
                        <button
                            className={`tab-buttons ${activeTab === 'parcels' ? 'active' : ''}`}
                            onClick={() => setActiveTab('parcels')}
                            style={{ width: '100%', height: '60px' }}
                        >
                            Parcels
                        </button>
                        <button
                            className={`tab-buttons ${activeTab === 'visitors' ? 'active' : ''}`}
                            onClick={() => setActiveTab('visitors')}
                            style={{ width: '100%', height: '60px' }}
                        >
                            Visitors
                        </button>
                    </div>
                    <div className="table-wrapper mt-[20px]">
                        {!selectedUnit ? (
                            <div className="info-text text-center md:text-left">No unit selected</div>
                        ) : activeTab === 'parcels' ? (
                            isPTableLoading ? (
                                <Loading />
                            ) : (
                                <>
                                    <div className="hidden md:block">
                                        <PageTable
                                            rows={currentRows}
                                            columns={[
                                                { label: 'Carrier', accessor: 'carrier', width: '20%' },
                                                { label: 'Tracking Number', accessor: 'trackingNumber', width: '20%' },
                                                { label: 'Description', accessor: 'description', width: '20%' },
                                                {
                                                label: 'Status',
                                                accessor: (row) => {
                                                    const statusColors = {
                                                        Pending: 'bg-blue-900',
                                                        Delivered: 'bg-green-500',
                                                        'Picked Up': 'bg-yellow-500',
                                                        Lost: 'bg-red-600',
                                                        Returned: 'bg-gray-500',
                                                        };
                                                    const colorClass = statusColors[row.status] || 'bg-gray-400';

                                                    return (
                                                        <span className={`text-white text-sm w-[100px] inline-block text-center py-1 rounded-full ${colorClass}`}>
                                                            {row.status}
                                                        </span>
                                                    );
                                                },
                                                width: '15%',
                                                },
                                                {
                                                label: 'Delivery Date',
                                                accessor: (row) => new Date(row.deliveryDate).toLocaleDateString(),
                                                width: '15%',
                                                },
                                            ]}
                                        />
                                    </div>
                                    <div className="md:hidden">
                                        <ParcelVisitorsCards activeTab={activeTab} rows={currentRows} />
                                    </div>
                                </>
                            )
                        ) : isVTableLoading ? (
                            <Loading />
                        ) : (
                            <>
                                <div className="hidden md:block">
                                    <PageTable
                                        rows={currentRows}
                                        columns={[
                                            { label: 'First Name', accessor: 'givenName', width: '20%' },
                                            { label: 'Last Name', accessor: 'lastName', width: '20%' },
                                            { label: 'Purpose', accessor: 'purpose', width: '20%' },
                                            {
                                                label: 'Check-In',
                                                accessor: (row) => new Date(row.checkIn).toLocaleDateString(),
                                                width: '15%',
                                            },
                                            {
                                                label: 'Check-Out',
                                                accessor: (row) =>
                                                row.checkOut ? new Date(row.checkOut).toLocaleDateString() : '—',
                                                width: '15%',
                                            },
                                        ]}
                                    />
                                </div>
                                <div className="md:hidden">
                                    <ParcelVisitorsCards activeTab={activeTab} rows={currentRows} />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="pagination-controls">
                            <div className="pagination-buttons">
                                <button
                                className="pagination-button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => (
                                <button
                                    key={i + 1}
                                    className={`pagination-page ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                                ))}

                                <button
                                    className="pagination-button"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
  );
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
    }));
};

const fetchUnits = async () => {
    const response = await axios.get(`${config.API_URL}/v2/unit`, {
        headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
    });
    return response.data;
};

const fetchParcelsByUnit = async (unitId) => {
  if (!unitId) return { results: [] };
  const response = await axios.get(`${config.API_URL}/v2/parcel?unit=${unitId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  console.log('fetchParcelsByUnit response:', response.data);
  return response.data;
};

const fetchVisitorsByUnit = async (unitId) => {
  if (!unitId) return [];
  const response = await axios.get(`${config.API_URL}/v2/visitor/unit/${unitId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  console.log('Visitors: ', response.data);
  return response.data;
};

export default ParcelsVisitors;
